import type { ViewedDocumentContext } from '../requests.types';

export type ResolveViewedDocumentInput = {
  artifactType?: string | null;
  tab?: string | null;
  documentId?: string | null;
  letterId?: string | null;
  noteContent?: string | null;
  documentContent?: string | null;
  documentTitle?: string | null;
  letterContent?: string | null;
  letterTitle?: string | null;
};

/**
 * The four session tab labels the resolver tells apart, resolved by the caller
 * from the session's tab vocabulary. Bounded strings, as the wire carries `tab`.
 */
type ViewedDocumentTabs = Readonly<
  Record<'note' | 'document' | 'prechart' | 'letter', string>
>;

const nonempty = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/**
 * Build the per-turn viewed-document payload. Returns undefined when
 * nothing is on screen as a document (no tab, empty body, transcript).
 */
export function resolveViewedDocument(
  input: ResolveViewedDocumentInput,
  tabs: ViewedDocumentTabs
): ViewedDocumentContext | undefined {
  const tab = input.tab ?? undefined;
  const artifactType = input.artifactType ?? undefined;

  if (artifactType === 'letter' || tab === tabs.letter) {
    const content = nonempty(input.letterContent);
    if (!content) return undefined;
    const title = nonempty(input.letterTitle);
    const letterId = nonempty(input.letterId);
    return {
      tab: tabs.letter,
      content,
      ...(letterId && { letter_id: letterId }),
      ...(title && { title }),
    };
  }

  if (artifactType === 'document' || tab === tabs.document || tab === tabs.prechart) {
    const content = nonempty(input.documentContent);
    if (!content) return undefined;
    const title = nonempty(input.documentTitle);
    const documentId = nonempty(input.documentId);
    return {
      tab: tab === tabs.prechart ? tabs.prechart : tabs.document,
      content,
      ...(documentId && { document_id: documentId }),
      ...(title && { title }),
    };
  }

  if (artifactType === 'note' || tab === tabs.note) {
    const content = nonempty(input.noteContent);
    if (!content) return undefined;
    return { tab: tabs.note, content };
  }

  return undefined;
}
